import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// @mui
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

/**
 * RHF-aware JSON editor: accepts an object value, renders monospace text, and
 * stores the parsed object on blur. Errors surface inline; invalid JSON keeps
 * the previous valid value but flags the textarea red.
 */
export default function RHFJsonField({ name, label, helperText, minRows = 4, ...other }) {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <JsonEditor
          field={field}
          label={label}
          helperText={helperText}
          error={error}
          minRows={minRows}
          {...other}
        />
      )}
    />
  );
}

function JsonEditor({ field, label, helperText, error, minRows, ...other }) {
  const [text, setText] = useState(() => stringify(field.value));
  const [parseError, setParseError] = useState('');

  useEffect(() => {
    setText(stringify(field.value));
  }, [field.value]);

  const onChange = (e) => {
    const next = e.target.value;
    setText(next);
    if (!next.trim()) {
      setParseError('');
      field.onChange(null);
      return;
    }
    try {
      const parsed = JSON.parse(next);
      setParseError('');
      field.onChange(parsed);
    } catch (err) {
      setParseError(err.message);
    }
  };

  const isError = !!error || !!parseError;

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <TextField
        value={text}
        onChange={onChange}
        multiline
        minRows={minRows}
        fullWidth
        error={isError}
        InputProps={{
          sx: { fontFamily: 'monospace', fontSize: 13 },
        }}
        {...other}
      />
      {(parseError || error?.message || helperText) && (
        <FormHelperText error={isError} sx={{ mx: 1.75 }}>
          {parseError || error?.message || helperText}
        </FormHelperText>
      )}
    </Box>
  );
}

function stringify(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

RHFJsonField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  helperText: PropTypes.string,
  minRows: PropTypes.number,
};

JsonEditor.propTypes = {
  field: PropTypes.object.isRequired,
  label: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.object,
  minRows: PropTypes.number,
};
